import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronLeft, Calendar, MapPin, PlusCircle, Sparkles } from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import BottomNav from '../components/BottomNav';
import UserAvatar from '../components/UserAvatar';

const STATUSES = [
  { k: 'all', label: 'TODOS' },
  { k: 'scheduled', label: 'Próximos' },
  { k: 'live', label: 'En vivo' },
  { k: 'finished', label: 'Finalizados' },
];

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleString('es-MX', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
}

export default function EventCalendarPage() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('all');
  const [city, setCity] = useState('');
  const [q, setQ] = useState('');

  const isOrganizer = useMemo(() => {
    const r = new Set(user?.roles || []);
    return r.has('organizer') || r.has('admin');
  }, [user]);

  const load = async () => {
    setLoading(true);
    try {
      const params = { limit: 60 };
      if (status !== 'all') params.status = status;
      if (city.trim()) params.city = city.trim();
      const { data } = await api.get('/events', { params });
      setItems(data.items || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [status]);

  const filtered = q.trim()
    ? items.filter((e) => (e.title + ' ' + (e.venue_name || '') + ' ' + e.city).toLowerCase().includes(q.toLowerCase()))
    : items;

  return (
    <div className="min-h-screen bg-aura-bg pb-28 relative">
      <div className="absolute inset-0 bg-aura-radial pointer-events-none" />
      <div className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-aura-violet/20 blur-3xl" />

      <header className="relative flex items-center justify-between px-5 pt-6">
        <button onClick={() => nav(-1)} className="text-aura-textDim flex items-center gap-1" data-testid="btn-back">
          <ChevronLeft size={20} />
        </button>
        <div className="font-display font-black tracking-[0.3em] aura-text-gradient text-sm">CALENDARIO AURA</div>
        <span className="w-8" />
      </header>

      <main className="relative mx-auto max-w-md px-5 pt-4 space-y-4">
        <section className="aura-card p-4 space-y-3">
          <div className="flex items-center gap-2">
            <Calendar size={16} className="text-aura-cyan" />
            <div className="text-white font-display font-bold text-sm tracking-wider">EVENTOS PROGRAMADOS</div>
          </div>
          <div className="flex gap-2 flex-wrap">
            {STATUSES.map((s) => (
              <button
                key={s.k}
                data-testid={`filter-${s.k}`}
                onClick={() => setStatus(s.k)}
                className={`text-[11px] font-display font-semibold px-3 py-1.5 rounded-full border ${status === s.k ? 'border-aura-cyan text-aura-cyan bg-aura-cyan/10' : 'border-white/10 text-white/70'}`}
              >
                {s.label}
              </button>
            ))}
          </div>
          <div className="flex gap-2">
            <input
              data-testid="filter-city"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              onBlur={load}
              placeholder="Ciudad…"
              className="flex-1 bg-aura-surface2 border border-white/10 rounded-xl px-3 py-2 text-sm text-white placeholder:text-aura-textDim outline-none focus:border-aura-cyan/60"
            />
            <input
              data-testid="filter-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar…"
              className="flex-1 bg-aura-surface2 border border-white/10 rounded-xl px-3 py-2 text-sm text-white placeholder:text-aura-textDim outline-none focus:border-aura-cyan/60"
            />
          </div>
        </section>

        {isOrganizer && (
          <Link
            to="/events/organizer"
            data-testid="btn-organizer-dashboard"
            className="aura-btn-primary w-full !py-3 justify-center"
          >
            <PlusCircle size={16} /> Panel del Organizador
          </Link>
        )}

        <Link
          to="/vote"
          data-testid="btn-vote-by-number"
          className="aura-btn-ghost w-full !py-3 justify-center border-aura-pink/40 hover:border-aura-pink"
        >
          <span className="text-aura-pink font-mono font-black text-sm tracking-[0.2em]">#EVT-</span>
          <span className="text-white font-display font-bold ml-1">Votar por número de evento</span>
        </Link>

        {loading ? (
          <div className="text-center py-16 text-aura-textDim animate-aura-pulse text-4xl">⚡</div>
        ) : filtered.length === 0 ? (
          <div className="aura-card p-8 text-center text-sm text-aura-textDim" data-testid="events-empty">
            No hay eventos con estos filtros.
          </div>
        ) : (
          <ul className="space-y-3" data-testid="events-list">
            {filtered.map((e) => (
              <li key={e.event_id}>
                <Link
                  to={`/events/${e.event_id}`}
                  data-testid={`event-card-${e.event_number}`}
                  className="block aura-card p-4 hover:border-aura-cyan/40 transition"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 text-[10px] font-mono">
                        <span className="text-aura-cyan tracking-[0.25em]">#EVT-{String(e.event_number).padStart(4, '0')}</span>
                        <span className={`px-2 py-0.5 rounded-full uppercase font-display font-bold ${
                          e.status === 'live' ? 'bg-aura-pink/20 text-aura-pink' :
                          e.status === 'scheduled' ? 'bg-aura-cyan/20 text-aura-cyan' :
                          e.status === 'finished' ? 'bg-white/10 text-white/70' :
                          'bg-red-500/20 text-red-400'
                        }`}>{e.status}</span>
                      </div>
                      <div className="font-display font-black text-white text-lg mt-1 truncate">{e.title}</div>
                      <div className="mt-1 text-xs text-aura-textDim flex items-center gap-1">
                        <Calendar size={12} /> {fmtDate(e.event_datetime)}
                      </div>
                      <div className="mt-0.5 text-xs text-aura-textDim flex items-center gap-1">
                        <MapPin size={12} /> {e.venue_name ? `${e.venue_name} · ` : ''}{e.city}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="font-mono font-bold text-aura-cyan text-xl">{e.contestants_count}</div>
                      <div className="text-[9px] uppercase tracking-widest text-aura-textDim">contendientes</div>
                    </div>
                  </div>
                  {e.organizer?.username && (
                    <div className="mt-3 flex items-center gap-2 border-t border-white/5 pt-3">
                      <UserAvatar user={e.organizer} size="sm" />
                      <div className="text-xs">
                        <span className="text-aura-textDim">Organiza </span>
                        <span className="text-white font-display font-semibold">@{e.organizer.username}</span>
                      </div>
                      {e.winners?.length > 0 && (
                        <span className="ml-auto inline-flex items-center gap-1 text-[10px] font-display font-bold text-aura-gold">
                          <Sparkles size={12} /> {e.winners.length} ganador{e.winners.length > 1 ? 'es' : ''}
                        </span>
                      )}
                    </div>
                  )}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </main>
      <BottomNav />
    </div>
  );
}
